<template>
  <section class="customer-overview-tab">
    <div class="customer-overview-tab__grid">
      <div class="customer-overview-tab__main">
        <article class="sale-summary-card sale-summary-card--financial customer-overview-health">
          <header class="sale-summary-card__header">
            <h4>{{ overviewText.health.title }}</h4>
            <span class="sale-summary-pill" :class="healthStatusClass">{{ healthStatusLabel }}</span>
          </header>

          <div class="customer-overview-health__content">
            <div class="sale-summary-ring" :style="ringStyle">
              <div class="sale-summary-ring__inner">
                <strong>{{ ringPercentLabel }}</strong>
                <small>{{ overviewText.health.liquidity }}</small>
              </div>
            </div>

            <div class="customer-overview-health__details">
              <div class="sale-summary-progress">
                <div class="sale-summary-progress__head">
                  <span>{{ overviewText.health.collectedVsPending }}</span>
                  <strong>{{ formatMoney(totalPaid) }} / {{ formatMoney(totalForecast) }}</strong>
                </div>

                <div class="sale-summary-progress__track">
                  <div class="sale-summary-progress__fill is-income" :style="collectedProgressStyle"></div>
                </div>
              </div>

              <p class="sale-summary-budget-health__copy customer-overview-health__summary">
                {{ healthSummaryText }}
              </p>

              <div v-if="showExposureWarning" class="customer-overview-warning">
                <strong>{{ overviewText.health.exposureWarningTitle }}</strong>
                <p>{{ exposureWarningText }}</p>
              </div>
            </div>
          </div>
        </article>

        <RecentActivityCard
          :entity-id="supplierId"
          entity-type="supplier"
          :per-page="4"
          @view-history="$emit('open-history')"
        />
      </div>

      <aside class="customer-overview-tab__side">
        <PaymentFlowCard
          :flow="overviewFlow"
          :title="overviewText.expenseFlow.title"
          :total-pending-label="overviewText.expenseFlow.totalPending"
          :overdue-label="overviewText.expenseFlow.overdue"
          :next7-days-label="overviewText.expenseFlow.next7Days"
          :rest-of-month-label="overviewText.expenseFlow.restOfMonth"
          top-border-color="#3bd42e"
        />
      </aside>
    </div>
  </section>
</template>

<script>
import { formatCurrency } from '@/utils/numberFormat';
import { toAmount } from '@/utils/numberValue';
import RecentActivityCard from '@/components/common/RecentActivityCard.vue';
import PaymentFlowCard from '@/components/common/PaymentFlowCard.vue';

export default {
  name: 'SupplierDetailOverviewTab',
  components: {
    RecentActivityCard,
    PaymentFlowCard,
  },
  props: {
    supplier: {
      type: Object,
      default: null,
    },
    supplierOverview: {
      type: Object,
      default: null,
    },
  },
  emits: ['open-history'],
  computed: {
    overviewText() {
      return {
        health: {
          title: 'Salud del proveedor',
          liquidity: 'Progreso de pago',
          collectedVsPending: 'Pagado vs pendiente',
          noForecastSummary: 'Aún no hay egresos registrados para evaluar la salud del proveedor.',
          status: {
            risk: 'Atención requerida',
            attention: 'Atención',
            healthy: 'Saludable',
          },
          exposureWarningTitle: 'Alerta de exposición',
        },
        expenseFlow: {
          title: 'Flujo de egresos',
          totalPending: 'Total pendiente',
          overdue: 'Vencidos',
          next7Days: 'Próximos 7 días',
          restOfMonth: 'Resto del mes',
        },
      };
    },
    supplierId() {
      return Number(this.supplier?.id || 0);
    },
    overviewHealth() {
      return this.supplierOverview?.health && typeof this.supplierOverview.health === 'object'
        ? this.supplierOverview.health
        : {};
    },
    overviewFlow() {
      return this.supplierOverview?.payment_flow && typeof this.supplierOverview.payment_flow === 'object'
        ? this.supplierOverview.payment_flow
        : {};
    },
    totalForecast() {
      return Number(this.overviewHealth?.forecast_total ?? this.supplier?.total_projected ?? 0) || 0;
    },
    totalPaid() {
      return Number(this.overviewHealth?.paid_total ?? this.supplier?.total_paid ?? 0) || 0;
    },
    pendingAmount() {
      return Math.max(this.totalForecast - this.totalPaid, 0);
    },
    collectedPercent() {
      if (this.totalForecast <= 0) {
        return 0;
      }

      return Math.max(0, Math.min(100, toAmount((this.totalPaid / this.totalForecast) * 100)));
    },
    pendingPercent() {
      return Math.max(0, Math.min(100, toAmount(100 - this.collectedPercent)));
    },
    ringPercentLabel() {
      return `${Math.round(this.collectedPercent)}%`;
    },
    ringStyle() {
      const percent = this.collectedPercent;
      const color = this.collectedPercent >= 85
        ? '#1f9d43'
        : (this.collectedPercent >= 70 ? '#d97706' : '#dc2626');

      return {
        background: `conic-gradient(${color} ${percent}%, #dbe2eb ${percent}% 100%)`,
      };
    },
    healthStatusLabel() {
      if (this.pendingPercent >= 35) {
        return this.overviewText.health.status.risk;
      }

      if (this.pendingPercent >= 15) {
        return this.overviewText.health.status.attention;
      }

      return this.overviewText.health.status.healthy;
    },
    healthStatusClass() {
      if (this.pendingPercent >= 35) {
        return 'is-danger';
      }

      if (this.pendingPercent >= 15) {
        return 'is-warning';
      }

      return 'is-success';
    },
    healthSummaryText() {
      if (this.totalForecast <= 0) {
        return this.overviewText.health.noForecastSummary;
      }

      return `Pendiente ${formatCurrency(this.pendingAmount)} (${this.pendingPercent.toFixed(1)}%) del total proyectado.`;
    },
    showExposureWarning() {
      return this.pendingPercent >= 15;
    },
    exposureWarningText() {
      return `El ${this.pendingPercent.toFixed(1)}% sigue pendiente de pago.`;
    },
    collectedProgressStyle() {
      return {
        width: `${this.collectedPercent}%`,
      };
    },
  },
  methods: {
    formatMoney(value) {
      return formatCurrency(Number(value || 0) || 0);
    },
  },
};
</script>
